import React from 'react';
import { Box, Typography, LinearProgress, Tooltip } from '@mui/material';
import { useMinIoFileStorageServiceGetApiFilesStorageInfo } from '../../api/generated/queries/queries';

interface StorageUsageBarProps {
  collapsed?: boolean;
}

const StorageUsageBar: React.FC<StorageUsageBarProps> = ({ collapsed = false }) => {
  const { data: storageInfo, isLoading, error } = useMinIoFileStorageServiceGetApiFilesStorageInfo();

  // If loading or error, don't render anything
  if (isLoading || error || !storageInfo) {
    return null;
  }

  // Extract storage information from the response
  // The API returns an object with storage details
  const usedSpace = (storageInfo as any)?.usedSpace || 0;
  const maxSpace = (storageInfo as any)?.maxSpace || 0;
  const percentageUsed = (storageInfo as any)?.percentageUsed || 0;
  const usedSpaceFormatted = (storageInfo as any)?.usedSpaceFormatted;
  const maxSpaceFormatted = (storageInfo as any)?.maxSpaceFormatted;

  // Format bytes to human readable format (fallback if formatted strings not available)
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Use formatted strings from backend if available, otherwise format manually
  const usedFormatted = usedSpaceFormatted || formatBytes(usedSpace);
  const totalFormatted = maxSpaceFormatted || formatBytes(maxSpace);

  // Determine color based on usage percentage
  const getProgressColor = (percentage: number) => {
    if (percentage < 70) return 'success';
    if (percentage < 90) return 'warning';
    return 'error';
  };

  const progressColor = getProgressColor(percentageUsed);

  if (collapsed) {
    // Show only a small indicator when collapsed
    return (
      <Tooltip title={`Przestrzeń dyskowa: ${usedFormatted} / ${totalFormatted} (${percentageUsed.toFixed(1)}%)`} placement="right">
        <Box sx={{ px: 1, py: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={percentageUsed}
            color={progressColor}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography
        variant="body2"
        fontSize={11}
        fontWeight={500}
        color="text.secondary"
        sx={{ mb: 0.5 }}
      >
        Przestrzeń dyskowa
      </Typography>
      
      <LinearProgress
        variant="determinate"
        value={percentageUsed}
        color={progressColor}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          mb: 0.5,
        }}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          variant="body2"
          fontSize={10}
          color="text.secondary"
        >
          {usedFormatted} / {totalFormatted}
        </Typography>
        <Typography
          variant="body2"
          fontSize={10}
          color={progressColor === 'error' ? 'error.main' : 'text.secondary'}
          fontWeight={500}
        >
          {percentageUsed.toFixed(1)}%
        </Typography>
      </Box>
    </Box>
  );
};

export default StorageUsageBar;